export function dimensions(value) {
  if (typeof value !== 'string' || value.length > 64) return null;
  const normalized = value.trim().toLowerCase().replace(/\binches?\b/g, 'in')
    .replace(/\u82f1\u5bf8/g, 'in').replace(/[\u2033\u201c\u201d"]/g, 'in').replace(/\s/g, '');
  if (!normalized.includes('in')) return null;
  const pair = normalized.match(/^(\d+(?:\.\d+)?)(?:in)?[lw]?[x\u00d7](\d+(?:\.\d+)?)(?:in)?[lw]?(?:in)?$/);
  if (!pair) return null;
  const values = [Number(pair[1]), Number(pair[2])];
  if (values.some(v => !Number.isFinite(v) || v <= 0 || v > 1000)) return null;
  return { key: [...values].sort((a, b) => a - b).join('x'), label: `${values[0]} \u00d7 ${values[1]} in` };
}

export function amazonDestination(value) {
  if (typeof value !== 'string' || value.length > 2048) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || !['amazon.com', 'www.amazon.com'].includes(url.hostname)
      || url.username || url.password || url.port) return null;
    const asin = url.pathname.match(/^\/(?:dp|gp\/product)\/([a-z0-9]{10})\/?$/i)?.[1]?.toUpperCase();
    const sellers = url.searchParams.getAll('m');
    if (!asin || sellers.length !== 1 || !/^[A-Z0-9]{10,32}$/.test(sellers[0])) return null;
    const safe = new URL(`https://www.amazon.com/dp/${asin}`);
    safe.searchParams.set('m', sellers[0]);
    safe.searchParams.set('th', '1');
    safe.searchParams.set('psc', '1');
    return { url: safe.href, asin, seller: sellers[0] };
  } catch { return null; }
}

export function resolveLink(config, context) {
  if (!config || !context || config.enabled !== 'true' || config.available !== 'true'
    || !/^\d+$/.test(config.product ?? '') || !/^\d+$/.test(config.variant ?? '')
    || context.product !== config.product || context.variant !== config.variant
    || !context.uniqueForm || !context.available || context.excluded || context.pending) return null;
  const configuredSize = dimensions(config.size);
  const selectedSize = dimensions(config.optionSize);
  const destination = amazonDestination(config.url);
  if (!configuredSize || !selectedSize || configuredSize.key !== selectedSize.key || !destination) return null;
  return { ...destination, size: configuredSize.label, product: config.product, variant: config.variant };
}

function readConfig(element) {
  const d = element?.dataset;
  return d ? { product: d.productId, variant: d.variantId, available: d.available,
    enabled: d.amazonEnabled, url: d.amazonUrl, size: d.amazonSize, optionSize: d.optionSize } : null;
}

if (globalThis.customElements && !customElements.get('amazon-product-link')) {
  class AmazonProductLink extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.root = this.closest('.shopify-section');
      this.wrapper = this.closest('.buy-buttons-block');
      this.config = readConfig(this);
      this.pending = false;
      this.generation = 0;
      const options = { signal: this.abort.signal };
      this.root?.addEventListener('variant:selected', () => {
        this.generation++;
        this.pending = true;
        this.render(null);
      }, options);
      this.root?.addEventListener('variant:update', event => {
        this.pending = true;
        this.render(null);
        const detail = event.detail;
        const product = String(detail?.data?.newProduct?.id ?? detail?.data?.productId ?? '');
        const variant = String(detail?.resource?.id ?? '');
        if (product !== this.dataset.productId || !/^\d+$/.test(variant)) return;
        // Use the freshly rendered variant data, never the previous size's mapping.
        const candidates = [...(detail?.data?.html?.querySelectorAll('amazon-product-link') ?? [])]
          .filter(el => el.dataset.productId === product && el.dataset.variantId === variant
            && !el.closest('product-card,quick-add-modal,dialog'));
        if (candidates.length !== 1) return;
        const config = readConfig(candidates[0]);
        const generation = this.generation;
        queueMicrotask(() => {
          if (!this.isConnected || generation !== this.generation) return;
          this.config = config;
          this.pending = false;
          this.refresh(variant, detail.resource.available === true);
        });
      }, options);
      this.addEventListener('click', event => {
        if (!event.target.closest('[data-amazon-link]')) return;
        const resolved = this.refresh();
        if (!resolved) { event.preventDefault(); return; }
        this.dispatchEvent(new CustomEvent('fba-pilot:outbound', {
          bubbles: true,
          detail: { productId: resolved.product, variantId: resolved.variant, asin: resolved.asin,
            previewOnly: this.dataset.themeRole !== 'main' }
        }));
      }, options);
      window.addEventListener('popstate', () => {
        this.generation++;
        this.pending = true;
        this.render(null);
      }, options);
      this.refresh();
    }

    disconnectedCallback() { this.abort?.abort(); }

    refresh(expectedVariant, expectedAvailable) {
      const forms = this.wrapper?.querySelectorAll('product-form-component form[action*="/cart/add"]') ?? [];
      const form = forms.length === 1 ? forms[0] : null;
      const inputs = form?.querySelectorAll('input[name="id"]') ?? [];
      const input = inputs.length === 1 ? inputs[0] : null;
      const submit = form?.querySelector('button[name="add"]');
      const variant = input?.value ?? '';
      const resolved = resolveLink(this.config, {
        product: form?.closest('product-form-component')?.dataset.productId,
        variant,
        uniqueForm: forms.length === 1 && inputs.length === 1,
        available: Boolean(submit && !submit.disabled && input && !input.disabled) && expectedAvailable !== false,
        pending: this.pending,
        excluded: Boolean(this.closest('product-card,quick-add-modal,dialog'))
          || (expectedVariant !== undefined && expectedVariant !== variant)
      });
      this.render(resolved);
      return resolved;
    }

    render(resolved) {
      const link = this.querySelector('[data-amazon-link]');
      this.hidden = !resolved;
      if (!link) return;
      link.setAttribute('aria-disabled', String(!resolved));
      link.tabIndex = resolved ? 0 : -1;
      if (resolved) {
        const label = `${this.dataset.buttonLabel} \u00b7 ${resolved.size}`;
        this.querySelector('[data-amazon-label]').textContent = label;
        link.setAttribute('aria-label', `${label}, ${this.dataset.newTabLabel}`);
        link.href = resolved.url;
      } else {
        link.removeAttribute('href');
      }
    }
  }
  customElements.define('amazon-product-link', AmazonProductLink);
}
